import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
  deleteWorkspace,
  listManagedWorkspaces,
  updateWorkspace,
} from "../../../api/workspaces";
import { listManagedWorkspaceBookings } from "../../../api/workspaceBookings";
import { listManagedWorkspaceLeases } from "../../../api/workspaceLeases";
import { useAuth } from "../../../context/AuthContext";
import {
  formatWorkspaceMoney,
  getWorkspaceCheckoutPath,
  getWorkspaceDisplayName,
  getWorkspaceListingPath,
  getWorkspaceLocation,
  titleCaseLabel,
} from "../../../utils/workspacePresentation";
import {
  getMarketplaceOperatorAgreementsPath,
  getMarketplaceOperatorWorkspaceAgreementsPath,
  getMarketplaceOperatorWorkspaceCompliancePath,
  getMarketplaceOperatorWorkspaceCreatePath,
  getMarketplaceOperatorWorkspaceEditPath,
  getMarketplaceOperatorWorkspaceOwnershipPath,
  getMarketplaceOperatorWorkspaceSupportPath,
  getMarketplaceOperatorWorkspaceWarningsPath,
} from "../../../utils/marketplaceOperatorRoutes";
import SBINModal from "../../../components/modals/SBINModal";
import InfoTooltip from "../../../components/common/InfoTooltip";
import { getAdvancedRecordTooltip } from "../../../components/workspaceCompliance/advancedRecordsHelpContent";
import RentalsShell from "./RentalsShell";

const tabs = [
  { id: "listings", label: "Listings" },
  { id: "agreements", label: "Rental Agreements" },
  { id: "availability", label: "Availability" },
  { id: "archive", label: "Archive" },
];

function statusTone(value) {
  const normalized = String(value || "").toLowerCase();
  if (["published", "active", "public", "confirmed", "approved"].includes(normalized)) {
    return "bg-emerald-50 text-emerald-700";
  }
  if (["draft", "hidden", "private"].includes(normalized)) return "bg-slate-100 text-slate-700";
  if (["invite_only", "pending", "requested"].includes(normalized)) return "bg-amber-50 text-amber-700";
  if (["archived", "inactive", "cancelled", "declined", "ended"].includes(normalized)) return "bg-rose-50 text-rose-700";
  return "bg-slate-100 text-slate-700";
}

function Pill({ children, value }) {
  return (
    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusTone(value)}`}>
      {children}
    </span>
  );
}

function EmptyState({ children }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center text-slate-600">
      {children}
    </div>
  );
}

function isDraftWorkspace(workspace) {
  return String(workspace?.rental_status || "").toLowerCase() === "draft";
}

function isArchivedWorkspace(workspace) {
  return ["archived", "inactive"].includes(
    String(workspace?.rental_status || "").toLowerCase()
  );
}

function isPublishedWorkspace(workspace) {
  return (
    String(workspace?.marketplace_status || "").toLowerCase() === "published" ||
    String(workspace?.visibility || "").toLowerCase() === "public" ||
    Boolean(workspace?.published_at)
  );
}

function getWorkspaceId(value) {
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function getRequestWorkspaceId(request) {
  return getWorkspaceId(
    request?.booth_id ??
      request?.workspace_id ??
      request?.legacy_workspace_id ??
      request?.legacy_booth_id
  );
}

function getAgreementWorkspaceId(agreement) {
  return getWorkspaceId(
    agreement?.workspace_id ??
      agreement?.booth_id ??
      agreement?.legacy_workspace_id ??
      agreement?.legacy_booth_id
  );
}

function isResolvedRentalStatus(status) {
  return [
    "cancelled",
    "canceled",
    "declined",
    "rejected",
    "ended",
    "terminated",
    "completed",
  ].includes(String(status || "").toLowerCase());
}

function isFutureDate(value) {
  if (!value) return false;
  const parsed = new Date(value);
  return !Number.isNaN(parsed.getTime()) && parsed.getTime() >= Date.now();
}

function DeleteDraftWorkspaceModal({
  workspace,
  isDeleting,
  error,
  onCancel,
  onConfirm,
}) {
  const isOpen = Boolean(workspace);
  const workspaceName = workspace ? getWorkspaceDisplayName(workspace) : "";
  return (
    <SBINModal
      isOpen={isOpen}
      onClose={onCancel}
      title="Delete Draft Workspace"
      size="sm"
      fitContent
      closeOnEscape={!isDeleting}
      closeOnBackdrop={!isDeleting}
      showClose={!isDeleting}
      footer={
        <div className="flex flex-wrap justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isDeleting}
            className="rounded-full border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Keep Draft
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isDeleting}
            className="rounded-full bg-rose-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isDeleting ? "Deleting..." : "Delete Draft"}
          </button>
        </div>
      }
    >
      <div className="space-y-4 text-sm text-slate-700">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-500">
            MyBoothShare Draft
          </p>
          <h3 className="mt-2 text-xl font-semibold text-slate-900">
            {workspaceName}
          </h3>
          <p className="mt-2 leading-6">
            This draft workspace has not been published. Deleting it removes the
            setup, photos, agreement draft, and listing details from your
            workspace inventory.
          </p>
        </div>
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-800">
          This action cannot be undone.
        </div>
        {workspace?.workspace_code ? (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <span className="font-semibold text-slate-900">Workspace code:</span>{" "}
            {workspace.workspace_code}
          </div>
        ) : null}
        {error ? (
          <div className="rounded-2xl border border-rose-200 bg-white px-4 py-3 text-rose-700">
            {error}
          </div>
        ) : null}
      </div>
    </SBINModal>
  );
}

function ArchiveWorkspaceModal({
  workspace,
  isArchiving,
  error,
  onCancel,
  onConfirm,
}) {
  const isOpen = Boolean(workspace);
  const workspaceName = workspace ? getWorkspaceDisplayName(workspace) : "";
  return (
    <SBINModal
      isOpen={isOpen}
      onClose={onCancel}
      title="Archive Workspace"
      size="sm"
      fitContent
      closeOnEscape={!isArchiving}
      closeOnBackdrop={!isArchiving}
      showClose={!isArchiving}
      footer={
        <div className="flex flex-wrap justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isArchiving}
            className="rounded-full border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Keep Workspace
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isArchiving}
            className="rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isArchiving ? "Archiving..." : "Archive"}
          </button>
        </div>
      }
    >
      <div className="space-y-4 text-sm text-slate-700">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
            Preserve Rental History
          </p>
          <h3 className="mt-2 text-xl font-semibold text-slate-900">
            {workspaceName}
          </h3>
          <p className="mt-2 leading-6">
            Archiving hides this workspace from active inventory and marketplace
            requests while keeping rental, payment, agreement, and support
            records available for history.
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
          Active or upcoming rentals must be resolved before a workspace can be archived.
        </div>
        {error ? (
          <div className="rounded-2xl border border-rose-200 bg-white px-4 py-3 text-rose-700">
            {error}
          </div>
        ) : null}
      </div>
    </SBINModal>
  );
}

export default function WorkspaceInventoryPage() {
  const { token, user } = useAuth();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const salonId =
    user?.salon_id ??
    (typeof window !== "undefined" ? Number(window.localStorage.getItem("salon_id")) || null : null);
  const [workspaces, setWorkspaces] = useState([]);
  const [requests, setRequests] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [archiveError, setArchiveError] = useState("");
  const [deletingWorkspaceId, setDeletingWorkspaceId] = useState(null);
  const [archivingWorkspaceId, setArchivingWorkspaceId] = useState(null);
  const [draftWorkspaceToDelete, setDraftWorkspaceToDelete] = useState(null);
  const [workspaceToArchive, setWorkspaceToArchive] = useState(null);
  const [advancedWorkspaceId, setAdvancedWorkspaceId] = useState(null);
  const [filters, setFilters] = useState({
    query: "",
    rentalStatus: "",
    marketplaceStatus: "",
    requestMode: "",
    availability: "",
  });
  const activeTab = tabs.some((tab) => tab.id === params.get("tab"))
    ? params.get("tab")
    : "listings";

  useEffect(() => {
    if (!token) return undefined;
    let active = true;
    setLoading(true);
    setError("");
    Promise.all([
      listManagedWorkspaces(token, salonId),
      listManagedWorkspaceBookings(token, salonId).catch(() => []),
      listManagedWorkspaceLeases(token, salonId).catch(() => []),
    ])
      .then(([workspaceRows, requestRows, agreementRows]) => {
        if (!active) return;
        setWorkspaces(Array.isArray(workspaceRows) ? workspaceRows : []);
        setRequests(Array.isArray(requestRows) ? requestRows : []);
        setAgreements(Array.isArray(agreementRows) ? agreementRows : []);
      })
      .catch((err) => {
        if (active) setError(err.message || "Unable to load workspaces.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token, salonId]);

  const workspaceMap = useMemo(
    () => new Map(workspaces.map((workspace) => [workspace.id, workspace])),
    [workspaces]
  );

  const historyByWorkspaceId = useMemo(() => {
    const next = new Map();
    const mark = (workspaceId, key) => {
      if (!workspaceId) return;
      const current = next.get(workspaceId) || {
        hasHistory: false,
        hasActiveFuture: false,
      };
      current[key] = true;
      if (key === "hasActiveFuture") current.hasHistory = true;
      next.set(workspaceId, current);
    };

    requests.forEach((request) => {
      const workspaceId = getRequestWorkspaceId(request);
      mark(workspaceId, "hasHistory");
      const status = request?.status;
      if (!isResolvedRentalStatus(status) && isFutureDate(request?.start_time || request?.start)) {
        mark(workspaceId, "hasActiveFuture");
      }
    });

    agreements.forEach((agreement) => {
      const workspaceId = getAgreementWorkspaceId(agreement);
      mark(workspaceId, "hasHistory");
      const status = agreement?.status;
      const hasFutureEnd = !agreement?.end_date || isFutureDate(agreement.end_date);
      if (!isResolvedRentalStatus(status) && hasFutureEnd) {
        mark(workspaceId, "hasActiveFuture");
      }
    });

    return next;
  }, [agreements, requests]);

  const filteredWorkspaces = useMemo(() => {
    return workspaces.filter((workspace) => {
      const name = getWorkspaceDisplayName(workspace).toLowerCase();
      const code = String(workspace.workspace_code || "").toLowerCase();
      const query = filters.query.trim().toLowerCase();
      if (query && !name.includes(query) && !code.includes(query)) return false;
      if (filters.rentalStatus && String(workspace.rental_status || "").toLowerCase() !== filters.rentalStatus) return false;
      if (filters.marketplaceStatus && String(workspace.marketplace_status || "").toLowerCase() !== filters.marketplaceStatus) return false;
      if (filters.requestMode && String(workspace.booking_mode || "").toLowerCase() !== filters.requestMode) return false;
      if (filters.availability === "active" && workspace.is_available === false) return false;
      if (filters.availability === "inactive" && workspace.is_available !== false) return false;
      return true;
    });
  }, [filters, workspaces]);

  const archivedWorkspaces = useMemo(
    () =>
      workspaces.filter((workspace) =>
        ["archived", "inactive"].includes(String(workspace.rental_status || "").toLowerCase())
      ),
    [workspaces]
  );

  const agreementRows = useMemo(() => {
    const requestRows = requests.map((request) => ({
      id: `request-${request.id}`,
      workspaceId: request.booth_id,
      renterName: request.renter_name || request.renter_email || "Unnamed renter",
      status: request.status || "requested",
      start: request.start_time,
      end: request.end_time,
      schedule: request.pricing_type || "request",
      deposit: request.deposit_amount,
      nextPayment: request.total_amount,
      kind: "Rental Request",
    }));
    const activeRows = agreements.map((agreement) => ({
      id: `agreement-${agreement.id}`,
      workspaceId: agreement.workspace_id,
      renterName: agreement.renter_name || agreement.renter_email || "Independent professional",
      status: agreement.status || "pending",
      start: agreement.start_date,
      end: agreement.end_date,
      schedule: agreement.billing_interval || agreement.recurrence || "monthly",
      deposit: agreement.deposit_amount,
      nextPayment: agreement.amount,
      kind: "Rental Agreement",
    }));
    return [...requestRows, ...activeRows];
  }, [agreements, requests]);

  const stats = useMemo(() => {
    const published = workspaces.filter((item) => item.marketplace_status === "published").length;
    const draft = workspaces.filter((item) => item.rental_status === "draft").length;
    const pending = requests.filter((item) =>
      ["pending", "requested"].includes(String(item.status || "").toLowerCase())
    ).length;
    const active = agreements.filter((item) =>
      ["approved", "active", "signed"].includes(String(item.status || "").toLowerCase())
    ).length;
    return { published, draft, pending, active };
  }, [agreements, requests, workspaces]);

  const setTab = (tab) => {
    const next = new URLSearchParams(params);
    next.set("tab", tab);
    setParams(next, { replace: true });
  };

  const getWorkspacePreviewPath = (workspace) => {
    const query = new URLSearchParams({
      preview: "1",
      returnTo: `${location.pathname}${location.search || ""}`,
    });
    return `${getWorkspaceListingPath(workspace)}?${query.toString()}`;
  };

  const handleRequestDeleteDraftWorkspace = (workspace) => {
    const history = historyByWorkspaceId.get(workspace?.id);
    if (
      !isDraftWorkspace(workspace) ||
      isPublishedWorkspace(workspace) ||
      history?.hasHistory
    ) {
      return;
    }
    setDeleteError("");
    setDraftWorkspaceToDelete(workspace);
  };

  const handleCancelDeleteDraftWorkspace = () => {
    if (deletingWorkspaceId) return;
    setDraftWorkspaceToDelete(null);
    setDeleteError("");
  };

  const handleConfirmDeleteDraftWorkspace = async () => {
    const workspace = draftWorkspaceToDelete;
    if (!isDraftWorkspace(workspace)) return;
    setDeleteError("");
    setDeletingWorkspaceId(workspace.id);
    try {
      await deleteWorkspace(workspace.id, token);
      setWorkspaces((prev) => prev.filter((item) => item.id !== workspace.id));
      setDraftWorkspaceToDelete(null);
    } catch (err) {
      setDeleteError(err.message || "Unable to delete draft workspace.");
    } finally {
      setDeletingWorkspaceId(null);
    }
  };

  const handleRequestArchiveWorkspace = (workspace) => {
    if (!workspace || isArchivedWorkspace(workspace)) return;
    setArchiveError("");
    setWorkspaceToArchive(workspace);
  };

  const handleCancelArchiveWorkspace = () => {
    if (archivingWorkspaceId) return;
    setWorkspaceToArchive(null);
    setArchiveError("");
  };

  const handleConfirmArchiveWorkspace = async () => {
    const workspace = workspaceToArchive;
    if (!workspace || isArchivedWorkspace(workspace)) return;
    setArchiveError("");
    setArchivingWorkspaceId(workspace.id);
    try {
      const updated = await updateWorkspace(
        workspace.id,
        { rental_status: "archived" },
        token
      );
      setWorkspaces((prev) =>
        prev.map((item) => (item.id === workspace.id ? updated : item))
      );
      setWorkspaceToArchive(null);
    } catch (err) {
      setArchiveError(
        err.message ||
          "Unable to archive workspace. Resolve active rentals first, then try again."
      );
    } finally {
      setArchivingWorkspaceId(null);
    }
  };

  const renderFilters = () => (
    <div className="grid gap-3 lg:grid-cols-[1.4fr,repeat(4,minmax(0,1fr))]">
      <input
        className="h-11 rounded-xl border border-slate-300 px-4 text-sm text-slate-900 shadow-sm"
        placeholder="Search workspace name or code"
        value={filters.query}
        onChange={(event) => setFilters((prev) => ({ ...prev, query: event.target.value }))}
      />
      <select
        className="h-11 rounded-xl border border-slate-300 px-4 text-sm text-slate-900 shadow-sm"
        value={filters.rentalStatus}
        onChange={(event) => setFilters((prev) => ({ ...prev, rentalStatus: event.target.value }))}
      >
        <option value="">All agreement states</option>
        <option value="draft">Draft</option>
        <option value="active">Active</option>
        <option value="archived">Archived</option>
      </select>
      <select
        className="h-11 rounded-xl border border-slate-300 px-4 text-sm text-slate-900 shadow-sm"
        value={filters.marketplaceStatus}
        onChange={(event) => setFilters((prev) => ({ ...prev, marketplaceStatus: event.target.value }))}
      >
        <option value="">All visibility states</option>
        <option value="hidden">Hidden</option>
        <option value="published">Published</option>
      </select>
      <select
        className="h-11 rounded-xl border border-slate-300 px-4 text-sm text-slate-900 shadow-sm"
        value={filters.requestMode}
        onChange={(event) => setFilters((prev) => ({ ...prev, requestMode: event.target.value }))}
      >
        <option value="">All request modes</option>
        <option value="request">Request</option>
        <option value="invite_only">Invite only</option>
        <option value="public">Public</option>
      </select>
      <select
        className="h-11 rounded-xl border border-slate-300 px-4 text-sm text-slate-900 shadow-sm"
        value={filters.availability}
        onChange={(event) => setFilters((prev) => ({ ...prev, availability: event.target.value }))}
      >
        <option value="">All availability</option>
        <option value="active">Active</option>
        <option value="inactive">Inactive</option>
      </select>
    </div>
  );

  const renderWorkspaceCard = (workspace) => {
    const history = historyByWorkspaceId.get(workspace.id) || {};
    const isDraft = isDraftWorkspace(workspace);
    const canDeleteDraft =
      isDraft &&
      !isPublishedWorkspace(workspace) &&
      !history.hasHistory;
    const canArchive = !isArchivedWorkspace(workspace) && !history.hasActiveFuture;
    const advancedOpen = advancedWorkspaceId === workspace.id;
    return (
    <article key={workspace.id} className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-500">
            {titleCaseLabel(workspace.workspace_type, "Workspace")}
            {workspace.workspace_code ? ` - ${workspace.workspace_code}` : ""}
          </p>
          <h3 className="text-xl font-semibold text-slate-900">{getWorkspaceDisplayName(workspace)}</h3>
          <p className="text-sm text-slate-600">{workspace.description || "No description added yet."}</p>
          {getWorkspaceLocation(workspace) ? (
            <p className="text-sm text-slate-500">{getWorkspaceLocation(workspace)}</p>
          ) : null}
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <Pill value={workspace.visibility}>{titleCaseLabel(workspace.visibility, "Private")}</Pill>
          <Pill value={workspace.marketplace_status}>{titleCaseLabel(workspace.marketplace_status, "Hidden")}</Pill>
        </div>
      </div>

      <div className="mt-5 grid gap-3 text-sm text-slate-700 md:grid-cols-4">
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Rent</div>
          <div className="mt-1 font-semibold text-slate-900">
            {workspace.price_per_week
              ? `${formatWorkspaceMoney(workspace.price_per_week, workspace.currency)} / week`
              : workspace.price_per_day
              ? `${formatWorkspaceMoney(workspace.price_per_day, workspace.currency)} / day`
              : `${formatWorkspaceMoney(workspace.price_per_hour, workspace.currency)} / hour`}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Request mode</div>
          <div className="mt-1 font-semibold text-slate-900">{titleCaseLabel(workspace.booking_mode, "Request")}</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Agreement status</div>
          <div className="mt-1 font-semibold text-slate-900">{titleCaseLabel(workspace.rental_status, "Draft")}</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Readiness</div>
          <div className="mt-1 font-semibold text-slate-900">{workspace.is_available === false ? "Inactive" : "Ready"}</div>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <Link to={getMarketplaceOperatorWorkspaceEditPath(workspace.id)} className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800">
          {isDraft ? "Continue Setup" : "Edit Setup"}
        </Link>
        {isDraft ? (
          <Link to={getWorkspacePreviewPath(workspace)} className="rounded-full border border-sky-300 px-4 py-2 text-sm font-semibold text-sky-700 hover:border-sky-400">
            Preview Listing
          </Link>
        ) : null}
        {!isDraft ? (
          <Link to={getMarketplaceOperatorAgreementsPath(`?workspaceId=${workspace.id}`)} className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-slate-400">
            Rental Agreement
          </Link>
        ) : null}
        {workspace.marketplace_status === "published" ? (
          <Link to={getWorkspaceListingPath(workspace)} className="rounded-full border border-emerald-300 px-4 py-2 text-sm font-semibold text-emerald-700 hover:border-emerald-400">
            View Listing
          </Link>
        ) : null}
        {history.hasActiveFuture ? (
          <Link to={getMarketplaceOperatorWorkspaceSupportPath(workspace.id)} className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-slate-400">
            Support & Follow-up
          </Link>
        ) : null}
        {canDeleteDraft ? (
          <button
            type="button"
            onClick={() => handleRequestDeleteDraftWorkspace(workspace)}
            disabled={deletingWorkspaceId === workspace.id}
            className="rounded-full border border-rose-300 px-4 py-2 text-sm font-semibold text-rose-700 hover:border-rose-400 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {deletingWorkspaceId === workspace.id ? "Deleting..." : "Delete Draft"}
          </button>
        ) : null}
        {!canDeleteDraft && canArchive ? (
          <button
            type="button"
            aria-label={`Archive ${getWorkspaceDisplayName(workspace)}`}
            onClick={() => handleRequestArchiveWorkspace(workspace)}
            disabled={archivingWorkspaceId === workspace.id}
            className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {archivingWorkspaceId === workspace.id ? "Archiving..." : "Archive"}
          </button>
        ) : null}
        {!canDeleteDraft && !canArchive && !isArchivedWorkspace(workspace) ? (
          <button
            type="button"
            aria-label={`Archive unavailable for ${getWorkspaceDisplayName(workspace)}`}
            disabled
            title="Resolve active or upcoming rentals before archiving this workspace."
            className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-400 disabled:cursor-not-allowed"
          >
            Archive unavailable
          </button>
        ) : null}
        <button
          type="button"
          aria-expanded={advancedOpen}
          aria-controls={`workspace-${workspace.id}-advanced-records`}
          onClick={() => setAdvancedWorkspaceId((current) => (current === workspace.id ? null : workspace.id))}
          className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-slate-400"
          title={getAdvancedRecordTooltip("Advanced setup and records")}
        >
          Advanced setup and records
        </button>
        <InfoTooltip
          text={getAdvancedRecordTooltip("Advanced setup and records")}
          label="Advanced setup and records"
          ariaLabel="More information"
          placement="bottom"
          maxWidth={320}
        />
      </div>
      {advancedOpen ? (
      <div id={`workspace-${workspace.id}-advanced-records`} className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-slate-800">Advanced setup and records</p>
            <p className="mt-1 text-xs text-slate-500">
              Use these only when you need deeper rental controls, agreement records, or follow-up history.
            </p>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-3">
          {[
            {
              label: "Setup Checks",
              to: getMarketplaceOperatorWorkspaceCompliancePath(workspace.id),
            },
            {
              label: "Client & Payment Control",
              to: getMarketplaceOperatorWorkspaceOwnershipPath(workspace.id),
            },
            {
              label: "Agreement Setup",
              to: getMarketplaceOperatorWorkspaceAgreementsPath(workspace.id),
            },
            {
              label: "Setup History",
              to: getMarketplaceOperatorWorkspaceWarningsPath(workspace.id),
            },
            {
              label: "Rental Request Preview",
              to: getWorkspaceCheckoutPath(workspace),
            },
            {
              label: "Support & Follow-up",
              to: getMarketplaceOperatorWorkspaceSupportPath(workspace.id),
            },
          ].map((item) => (
            <span key={item.label} className="inline-flex items-center gap-1">
              <Link
                to={item.to}
                title={getAdvancedRecordTooltip(item.label)}
                className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-slate-400"
              >
                {item.label}
              </Link>
              <InfoTooltip
                text={getAdvancedRecordTooltip(item.label)}
                label={item.label}
                ariaLabel="More information"
                placement="bottom"
                maxWidth={300}
              />
            </span>
          ))}
        </div>
      </div>
      ) : null}
    </article>
    );
  };

  const renderTab = () => {
    if (activeTab === "agreements") {
      return (
        <div className="space-y-4">
          {agreementRows.map((row) => {
            const workspace = workspaceMap.get(row.workspaceId);
            return (
              <article key={row.id} className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-500">{row.kind}</p>
                    <h3 className="mt-2 text-xl font-semibold text-slate-900">{row.renterName}</h3>
                    <p className="mt-1 text-sm text-slate-600">{getWorkspaceDisplayName(workspace)}</p>
                  </div>
                  <Pill value={row.status}>{titleCaseLabel(row.status, "Requested")}</Pill>
                </div>
                <div className="mt-4 grid gap-3 text-sm text-slate-700 md:grid-cols-5">
                  <div><span className="font-semibold">Start:</span> {row.start || "TBD"}</div>
                  <div><span className="font-semibold">End:</span> {row.end || "Ongoing"}</div>
                  <div><span className="font-semibold">Payment schedule:</span> {titleCaseLabel(row.schedule)}</div>
                  <div><span className="font-semibold">Deposit:</span> {row.deposit ? formatWorkspaceMoney(row.deposit) : "Not required"}</div>
                  <div><span className="font-semibold">Next due:</span> {row.nextPayment ? formatWorkspaceMoney(row.nextPayment) : "TBD"}</div>
                </div>
              </article>
            );
          })}
          {!agreementRows.length ? <EmptyState>No rental agreements or requests yet.</EmptyState> : null}
        </div>
      );
    }
    if (activeTab === "availability") {
      return (
        <div className="grid gap-4 xl:grid-cols-2">
          {filteredWorkspaces.map((workspace) => (
            <article key={workspace.id} className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">{getWorkspaceDisplayName(workspace)}</h3>
                  <p className="mt-1 text-sm text-slate-600">
                    {workspace.available_start_time || "Open"} to {workspace.available_end_time || "Close"}; buffer {workspace.buffer_minutes ?? 15} minutes
                  </p>
                </div>
                <Pill value={workspace.is_available === false ? "inactive" : "active"}>
                  {workspace.is_available === false ? "Inactive" : "Available"}
                </Pill>
              </div>
              <Link to={getMarketplaceOperatorWorkspaceEditPath(workspace.id)} className="mt-4 inline-flex rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-slate-400">
                Edit Availability
              </Link>
            </article>
          ))}
        </div>
      );
    }
    if (activeTab === "archive") {
      return (
        <div className="grid gap-4 xl:grid-cols-2">
          {archivedWorkspaces.map(renderWorkspaceCard)}
          {!archivedWorkspaces.length ? <EmptyState>No archived workspaces.</EmptyState> : null}
        </div>
      );
    }
    return (
      <div className="grid gap-4 xl:grid-cols-2">
        {filteredWorkspaces.map(renderWorkspaceCard)}
        {!filteredWorkspaces.length ? <EmptyState>No workspaces match the current filters.</EmptyState> : null}
      </div>
    );
  };

  return (
    <RentalsShell
      title="Workspaces"
      description="Manage rental listings, agreement activity, availability, and archived workspaces from one operator area."
      actions={
        <Link to={getMarketplaceOperatorWorkspaceCreatePath()} className="rounded-full bg-rose-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-600">
          List a Workspace
        </Link>
      }
    >
      <section className="grid gap-4 md:grid-cols-4">
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Published Workspaces</p>
          <p className="mt-3 text-3xl font-semibold text-slate-900">{stats.published}</p>
        </article>
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Draft Workspaces</p>
          <p className="mt-3 text-3xl font-semibold text-slate-900">{stats.draft}</p>
        </article>
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Pending Requests</p>
          <p className="mt-3 text-3xl font-semibold text-slate-900">{stats.pending}</p>
        </article>
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Active Agreements</p>
          <p className="mt-3 text-3xl font-semibold text-slate-900">{stats.active}</p>
        </article>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-6 py-5">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setTab(tab.id)}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                  activeTab === tab.id
                    ? "bg-rose-500 text-white"
                    : "border border-slate-300 text-slate-700 hover:border-slate-400"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="mt-5">{renderFilters()}</div>
        </div>
        <div className="px-6 py-6">
          {loading ? <p className="text-slate-600">Loading workspaces...</p> : null}
          {error ? <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}
          {deleteError ? <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{deleteError}</div> : null}
          {archiveError ? <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{archiveError}</div> : null}
          {!loading && !error ? renderTab() : null}
        </div>
      </section>
      <DeleteDraftWorkspaceModal
        workspace={draftWorkspaceToDelete}
        isDeleting={Boolean(
          draftWorkspaceToDelete &&
            deletingWorkspaceId === draftWorkspaceToDelete.id
        )}
        error={deleteError}
        onCancel={handleCancelDeleteDraftWorkspace}
        onConfirm={handleConfirmDeleteDraftWorkspace}
      />
      <ArchiveWorkspaceModal
        workspace={workspaceToArchive}
        isArchiving={Boolean(
          workspaceToArchive &&
            archivingWorkspaceId === workspaceToArchive.id
        )}
        error={archiveError}
        onCancel={handleCancelArchiveWorkspace}
        onConfirm={handleConfirmArchiveWorkspace}
      />
    </RentalsShell>
  );
}
